import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import * as mui from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { createPortal } from 'react-dom';

// Styled components for modern select
const SelectContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  marginBottom: theme.spacing(1),
}));

const SelectButton = styled(mui.Button)(({ theme, open, empty, error }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  minHeight: '48px',
  padding: '10px 14px',
  background: 'white',
  color: empty ? alpha(theme.palette.text.primary, 0.4) : theme.palette.text.primary,
  border: `1px solid ${error ? theme.palette.error.main : open ? alpha(theme.palette.primary.main, 0.7) : alpha(theme.palette.divider, 0.6)}`,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  textAlign: 'left',
  '&:hover': {
    borderColor: error ? theme.palette.error.main : alpha(theme.palette.primary.main, 0.5),
    backgroundColor: 'white',
  },
  boxShadow: open ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
  '& .MuiButton-endIcon': {
    marginRight: 0,
    marginLeft: 8,
  },
}));

const SelectDropdown = styled(mui.Paper)(({ theme }) => ({
  position: 'absolute',
  zIndex: 9999,
  maxHeight: '300px',
  overflowY: 'auto',
  padding: theme.spacing(1),
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  borderRadius: theme.shape.borderRadius,
  '&:empty': {
    display: 'none'
  },
  visibility: 'visible !important',
  '& *': {
    overflow: 'visible',
  }
}));

const OptionItem = styled(mui.MenuItem)(({ theme, isselected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 12px',
  marginBottom: '2px',
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  color: isselected === 'true' ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: isselected === 'true' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  fontWeight: isselected === 'true' ? 500 : 400,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  whiteSpace: 'normal',
  wordBreak: 'break-word',
}));

const SelectedItemsDisplay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  maxWidth: 'calc(100% - 24px)', // Leave space for the dropdown icon
  overflow: 'hidden',
  wordBreak: 'break-word',
}));

const SelectedItemChip = styled('span')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  borderRadius: '4px',
  padding: '2px 6px',
  fontSize: '0.8125rem',
  marginRight: '4px',
  marginBottom: '2px',
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const ErrorText = styled(mui.Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  marginTop: '4px',
  marginLeft: '14px',
}));

const ModernSelect = ({
  label,
  placeholderText,
  selectOptions = [],
  selectedOptions = [],
  value,
  handleChange,
  isMultiSelect = false,
  defaultValue,
  error = false,
  helperText = '',
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Toggle dropdown state when clicked
  const handleClick = () => {
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  // Position dropdown to ensure it's visible
  const positionDropdown = () => {
    if (!open || !dropdownRef.current || !containerRef.current) return;
    
    const dropdown = dropdownRef.current;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Use fixed positioning with document.body coordinates
    dropdown.style.position = 'fixed';
    dropdown.style.minWidth = `${rect.width}px`;
    dropdown.style.width = `${rect.width}px`;
    
    // Calculate available space
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Set horizontal position
    dropdown.style.left = `${rect.left}px`;
    
    // Determine if dropdown should appear above or below
    const dropdownHeight = Math.min(300, selectOptions.length * 40 + 16); // Estimate height
    
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      // Position above if there's more space above than below
      dropdown.style.top = 'auto';
      dropdown.style.bottom = `${viewportHeight - rect.top + 4}px`;
    } else {
      // Position below with potential height constraint
      dropdown.style.top = `${rect.bottom + 4}px`;
      dropdown.style.bottom = 'auto';
      
      if (spaceBelow < dropdownHeight) {
        // If not enough space below, constrain height
        dropdown.style.maxHeight = `${spaceBelow - 20}px`;
      }
    }
  };

  // Use useLayoutEffect to position the dropdown immediately after render
  useLayoutEffect(() => {
    if (open && dropdownRef.current) {
      positionDropdown();
    }
  }, [open]);

  // Update dropdown position whenever selectOptions changes too
  useEffect(() => {
    if (open && dropdownRef.current) {
      positionDropdown();
    }
  }, [open, selectOptions.length]);

  // Handle window resize and scrolling to reposition dropdown if needed
  useEffect(() => {
    if (!open) return; // Only add listeners when dropdown is open
    
    const handleResize = () => {
      positionDropdown();
    };
    
    const handleScroll = () => {
      positionDropdown();
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase for scroll
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    if (!open) return; // Only add listener when dropdown is open
    
    const handleClickOutside = (event) => {
      // Check if click was outside the container and dropdown
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    // Use capture phase to ensure our handler runs first
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [open]);

  const handleOptionClick = (option) => {
    let newSelectedOptions;

    if (isMultiSelect) {
      // For multi-select, toggle the selected option
      const isAlreadySelected = selectedOptions && Array.isArray(selectedOptions) && selectedOptions.some(
        (selected) => selected.value === option.value
      );

      if (isAlreadySelected) {
        newSelectedOptions = selectedOptions.filter(
          (selected) => selected.value !== option.value
        );
      } else {
        newSelectedOptions = [...(Array.isArray(selectedOptions) ? selectedOptions : []), option];
      }
    } else {
      // For single select, replace the selection
      newSelectedOptions = option;
      handleClose();
    }

    handleChange(newSelectedOptions);
  };

  // Determine what to display in the select button
  const getDisplayText = () => {
    if (!selectedOptions || (Array.isArray(selectedOptions) && selectedOptions.length === 0)) {
      return placeholderText || 'Select';
    }

    if (isMultiSelect) {
      if (Array.isArray(selectedOptions) && selectedOptions.length === 1) {
        return selectedOptions[0].label;
      }
      return Array.isArray(selectedOptions) ? `${selectedOptions.length} items selected` : placeholderText;
    }

    return selectedOptions.label || placeholderText;
  };

  const renderSelectedItems = () => {
    if (!isMultiSelect || !selectedOptions || (Array.isArray(selectedOptions) && selectedOptions.length === 0)) {
      return getDisplayText();
    }

    if (!Array.isArray(selectedOptions)) {
      return placeholderText || 'Select';
    }

    if (selectedOptions.length === 1) {
      return selectedOptions[0].label;
    }

    return (
      <SelectedItemsDisplay>
        {selectedOptions.length <= 2 ? (
          selectedOptions.map((option) => (
            <SelectedItemChip key={option.value}>
              {option.label}
            </SelectedItemChip>
          ))
        ) : (
          <>
            <SelectedItemChip>
              {selectedOptions[0].label}
            </SelectedItemChip>
            <SelectedItemChip>
              +{selectedOptions.length - 1} more
            </SelectedItemChip>
          </>
        )}
      </SelectedItemsDisplay>
    );
  };

  const isSelected = (option) => {
    if (!selectedOptions) return false;
    
    if (isMultiSelect) {
      return Array.isArray(selectedOptions) && selectedOptions.some((selected) => selected.value === option.value);
    }
    
    return selectedOptions.value === option.value;
  };

  return (
    <SelectContainer ref={containerRef}>
      {label && (
        <mui.Typography
          variant="body2"
          component="label"
          sx={{ display: 'block', mb: 0.5, fontWeight: 500, color: 'text.secondary' }}
        >
          {label}{required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
        </mui.Typography>
      )}
      
      <SelectButton
        disableRipple
        empty={
          isMultiSelect
            ? !selectedOptions || (Array.isArray(selectedOptions) && selectedOptions.length === 0)
            : !selectedOptions || !selectedOptions.value
        }
        open={open}
        error={error}
        onClick={handleClick}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
      >
        {renderSelectedItems()}
      </SelectButton>
      
      {open && (
        createPortal(
          <SelectDropdown ref={dropdownRef}>
            {selectOptions.map((option) => (
              <OptionItem
                key={option.value}
                onClick={() => handleOptionClick(option)}
                disableRipple
                isselected={isSelected(option).toString()}
              >
                {isMultiSelect && (
                  <mui.Box 
                    component="span" 
                    sx={{ 
                      mr: 1.5, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      border: isSelected(option) ? 'none' : '1px solid rgba(0,0,0,0.2)',
                      borderRadius: '2px',
                      backgroundColor: isSelected(option) ? 'primary.main' : 'transparent',
                    }}
                  >
                    {isSelected(option) && (
                      <CheckIcon sx={{ fontSize: '0.75rem', color: 'white' }} />
                    )}
                  </mui.Box>
                )}
                {option.label}
              </OptionItem>
            ))}
          </SelectDropdown>,
          document.body
        )
      )}
      
      {error && helperText && (
        <ErrorText>{helperText}</ErrorText>
      )}
    </SelectContainer>
  );
};

export default ModernSelect; 